import { useCallback, useMemo, useReducer } from 'react';
import {
  checkPlacement,
  computeLoadStats,
  dropToRest,
  previewPlacement,
  rotateBox,
  settleAll,
  snapToGrid,
  DEFAULT_GRID_CM,
  type ContainerBounds,
  type LayoutBox,
  type LoadStats,
  type PlacementCheck,
} from '../utils/container-layout';

/**
 * Estado del acomodo manual de un contenedor, con historial de deshacer/rehacer.
 *
 * Separa la lógica de la escena 3D para poder testearla sin montar three.js.
 * Toda mutación pasa por el reducer, así el historial no se puede desincronizar.
 */

/** Cuántos pasos de deshacer se recuerdan. Suficiente para una sesión de acomodo. */
const MAX_HISTORY = 50;

interface LayoutState {
  /** Cajas colocadas dentro del contenedor. */
  placed: LayoutBox[];
  /** Cajas todavía sin ubicar — la bandeja lateral. */
  tray: LayoutBox[];
  past: Array<{ placed: LayoutBox[]; tray: LayoutBox[] }>;
  future: Array<{ placed: LayoutBox[]; tray: LayoutBox[] }>;
  /**
   * Lo último guardado, con las mismas referencias. Hay cambios sin guardar
   * cuando lo que se ve no es eso.
   *
   * 🔴 Antes era una marca que cualquier deshacer prendía: deshacer hasta lo
   * guardado seguía diciendo «Guardar acomodo» (auditoría de Comercia, 29-oct).
   */
  saved: { placed: LayoutBox[]; tray: LayoutBox[] };
}

type Snapshot = { placed: LayoutBox[]; tray: LayoutBox[] };

type LayoutAction =
  | { type: 'reset'; placed: LayoutBox[]; tray: LayoutBox[] }
  | { type: 'commit'; placed: LayoutBox[]; tray: LayoutBox[] }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'saved'; snapshot?: Snapshot };

function snapshot(state: LayoutState): Snapshot {
  return { placed: state.placed, tray: state.tray };
}

function isDirty(state: LayoutState): boolean {
  return state.placed !== state.saved.placed || state.tray !== state.saved.tray;
}

function reducer(state: LayoutState, action: LayoutAction): LayoutState {
  switch (action.type) {
    case 'reset':
      return {
        placed: action.placed,
        tray: action.tray,
        past: [],
        future: [],
        saved: { placed: action.placed, tray: action.tray },
      };

    case 'commit':
      return {
        ...state,
        placed: action.placed,
        tray: action.tray,
        past: [...state.past, snapshot(state)].slice(-MAX_HISTORY),
        future: [], // una acción nueva descarta el rehacer
      };

    case 'undo': {
      const previous = state.past[state.past.length - 1];
      if (!previous) return state;
      return {
        ...state,
        placed: previous.placed,
        tray: previous.tray,
        past: state.past.slice(0, -1),
        future: [snapshot(state), ...state.future].slice(0, MAX_HISTORY),
      };
    }

    case 'redo': {
      const next = state.future[0];
      if (!next) return state;
      return {
        ...state,
        placed: next.placed,
        tray: next.tray,
        past: [...state.past, snapshot(state)].slice(-MAX_HISTORY),
        future: state.future.slice(1),
      };
    }

    case 'saved':
      return { ...state, saved: action.snapshot ?? snapshot(state) };
  }
}

export interface UseContainerLayoutResult {
  placed: LayoutBox[];
  tray: LayoutBox[];
  stats: LoadStats;
  canUndo: boolean;
  canRedo: boolean;
  dirty: boolean;

  /** Simula soltar una caja en (x, y) sin aplicar el cambio. */
  preview: (
    id: string,
    targetX: number,
    targetY: number
  ) => { box: LayoutBox; check: PlacementCheck } | null;
  /** Mueve una caja ya colocada. Devuelve `false` si la posición es inválida. */
  moveTo: (id: string, targetX: number, targetY: number) => boolean;
  /** Rota 90°. Si al rotar la posición deja de ser válida, no aplica. */
  rotate: (id: string) => boolean;
  /** Empuja una caja con el teclado. */
  nudge: (id: string, deltaX: number, deltaY: number) => boolean;
  /** Saca una caja del contenedor y la manda a la bandeja. */
  unplace: (id: string) => void;
  /** Mete una caja de la bandeja en la primera posición libre. */
  place: (id: string, targetX?: number, targetY?: number) => boolean;
  /** Reemplaza todo el acomodo — lo usa el botón de auto-acomodar. */
  applyLayout: (placed: LayoutBox[], tray: LayoutBox[]) => void;
  /**
   * Vuelve a arrancar desde cero con otra carga. Descarta el historial y los
   * cambios pendientes: se usa cuando cambian los items del contenedor, no para
   * deshacer.
   */
  reset: (placed: LayoutBox[], tray: LayoutBox[]) => void;
  undo: () => void;
  redo: () => void;
  /**
   * Marca como guardado lo que se ve o, si se pasa, lo que se mandó a guardar:
   * así un cambio hecho mientras se guardaba sigue pendiente.
   */
  markSaved: (sent?: Snapshot) => void;
}

export function useContainerLayout(
  initialPlaced: LayoutBox[],
  initialTray: LayoutBox[],
  bounds: ContainerBounds,
  gridCm: number = DEFAULT_GRID_CM
): UseContainerLayoutResult {
  const [state, dispatch] = useReducer(reducer, {
    placed: initialPlaced,
    tray: initialTray,
    past: [],
    future: [],
    saved: { placed: initialPlaced, tray: initialTray },
  });

  const stats = useMemo(() => computeLoadStats(state.placed, bounds), [state.placed, bounds]);

  const preview = useCallback(
    (id: string, targetX: number, targetY: number) => {
      const box = state.placed.find((b) => b.id === id) ?? state.tray.find((b) => b.id === id);
      if (!box) return null;
      const others = state.placed.filter((b) => b.id !== id);
      return previewPlacement(box, targetX, targetY, others, bounds, gridCm);
    },
    [state.placed, state.tray, bounds, gridCm]
  );

  const moveTo = useCallback(
    (id: string, targetX: number, targetY: number) => {
      const result = preview(id, targetX, targetY);
      if (!result || !result.check.valid) return false;
      dispatch({
        type: 'commit',
        placed: state.placed.map((b) => (b.id === id ? result.box : b)),
        tray: state.tray,
      });
      return true;
    },
    [preview, state.placed, state.tray]
  );

  const rotate = useCallback(
    (id: string) => {
      const box = state.placed.find((b) => b.id === id);
      if (!box) return false;
      const others = state.placed.filter((b) => b.id !== id);

      // Rotar es una operación EN EL LUGAR, no un movimiento: se valida a la
      // altura actual. Si al rotar chocaría con una vecina, falla — no se la
      // deja "saltar" encima, que sería un salto inesperado para el usuario.
      const rotated = rotateBox(box);
      if (!checkPlacement(rotated, others, bounds).valid) return false;

      // Ya validada, puede caer si al rotar perdió el apoyo de abajo.
      const settled = dropToRest(rotated, others);

      dispatch({
        type: 'commit',
        placed: state.placed.map((b) => (b.id === id ? settled : b)),
        tray: state.tray,
      });
      return true;
    },
    [state.placed, state.tray, bounds]
  );

  const nudge = useCallback(
    (id: string, deltaX: number, deltaY: number) => {
      const box = state.placed.find((b) => b.id === id);
      if (!box) return false;
      return moveTo(id, box.x + deltaX, box.y + deltaY);
    },
    [state.placed, moveTo]
  );

  const unplace = useCallback(
    (id: string) => {
      const box = state.placed.find((b) => b.id === id);
      if (!box) return;
      // Lo que quedaba encima tiene que bajar.
      const remaining = settleAll(state.placed.filter((b) => b.id !== id));
      dispatch({
        type: 'commit',
        placed: remaining,
        tray: [...state.tray, { ...box, x: 0, y: 0, z: 0 }],
      });
    },
    [state.placed, state.tray]
  );

  const place = useCallback(
    (id: string, targetX?: number, targetY?: number) => {
      const box = state.tray.find((b) => b.id === id);
      if (!box) return false;

      // Con posición pedida: se intenta ahí. Sin posición: primer hueco de la grilla.
      let landing: LayoutBox | null = null;
      if (targetX !== undefined && targetY !== undefined) {
        const r = previewPlacement(box, targetX, targetY, state.placed, bounds, gridCm);
        if (r.check.valid) landing = r.box;
      } else {
        outer: for (let y = 0; y + box.widthCm <= bounds.widthCm; y += gridCm) {
          for (let x = 0; x + box.lengthCm <= bounds.lengthCm; x += gridCm) {
            const r = previewPlacement(box, x, y, state.placed, bounds, gridCm);
            if (r.check.valid) {
              landing = r.box;
              break outer;
            }
          }
        }
      }

      if (!landing) return false;
      dispatch({
        type: 'commit',
        placed: [...state.placed, landing],
        tray: state.tray.filter((b) => b.id !== id),
      });
      return true;
    },
    [state.placed, state.tray, bounds, gridCm]
  );

  const applyLayout = useCallback((placed: LayoutBox[], tray: LayoutBox[]) => {
    dispatch({ type: 'commit', placed, tray });
  }, []);

  const reset = useCallback((placed: LayoutBox[], tray: LayoutBox[]) => {
    dispatch({ type: 'reset', placed, tray });
  }, []);

  return {
    placed: state.placed,
    tray: state.tray,
    stats,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
    dirty: isDirty(state),
    preview,
    moveTo,
    rotate,
    nudge,
    unplace,
    place,
    applyLayout,
    reset,
    undo: useCallback(() => dispatch({ type: 'undo' }), []),
    redo: useCallback(() => dispatch({ type: 'redo' }), []),
    markSaved: useCallback((sent?: Snapshot) => dispatch({ type: 'saved', snapshot: sent }), []),
  };
}

/** Reexportado para que los consumidores no tengan que importar de dos lados. */
export { snapToGrid, DEFAULT_GRID_CM };
export type { LayoutBox, ContainerBounds, LoadStats };
